package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminClient
import org.gymly.model.AdminClientAccountApplyCredit
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminMembershipOrderUpsert
import org.gymly.model.AdminBillingPeriod
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminClientWalletDepositBatch
import org.gymly.model.AdminClientWalletDepositItem
import org.gymly.model.AdminPaymentCollect
import org.gymly.model.AdminPaymentCollectLine
import org.gymly.model.AdminPaymentCollectTarget
import org.gymly.model.AdminPaymentDiscount
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminTrainer
import org.gymly.theme.AdminColors
import org.gymly.ui.components.AdminDateField
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage

/** Cómo se cobra la lista: cada socio con su pago, o un pago único que cubre a todos. */
// DEUDA TÉCNICA: Shared (pago único) ya no se puede elegir en pantalla. Ver docs/deuda-tecnica.md
enum class BulkPaymentMode { PerClient, Shared }

data class BulkPaymentQueueItem(
    val client: AdminClient,
    val planId: String,
    val planLabel: String,
    val trainerId: String?,
    val trainerLabel: String,
    val paidAt: String = "",
    val startsAt: String,
    val endsAt: String = "",
    val isCourtesy: Boolean,
    val amountCents: Long,
    /** Saldo de monedero reservado en UI; se aplica al crear la orden. */
    val walletApplyCents: Long = 0L,
    /** Cobro propio del socio (modo por socio); vacío en modo pago único. */
    val paymentLines: List<PaymentLineDraft> = emptyList(),
    val changeGivenCents: Long = 0L,
    val changeGivenUsdCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    val pendingBsCents: Long = 0L,
    /** Descuento en Bs de esta persona; se aplica a su orden dentro del cobro. */
    val discount: AdminPaymentDiscount? = null,
    val paymentConfigured: Boolean = false,
    var result: String? = null,
    var ok: Boolean? = null,
    var orderId: String? = null,
) {
    val remainingCents: Long
        get() = if (isCourtesy) {
            0L
        } else {
            bulkNetDueBsCents(amountCents - walletApplyCents.coerceAtLeast(0L), pendingBsCents, discount?.cents ?: 0L)
        }

    /** El socio ya tiene resuelto su cobro: cortesía, monedero completo o métodos cargados. */
    val settled: Boolean
        get() = isCourtesy || remainingCents <= 0L || (paymentConfigured && paymentLines.isNotEmpty())
}

@Composable
fun BulkPaymentOnlyPanel(
    clients: List<AdminClient>,
    plans: List<AdminMembershipPlanItem>,
    trainers: List<AdminTrainer>,
    companyId: String?,
    processing: Boolean,
    onProcessingChange: (Boolean) -> Unit,
    onProgress: (done: Int, total: Int) -> Unit,
    onDoneMessage: (String) -> Unit,
    onError: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var query by remember { mutableStateOf("") }
    var selected by remember { mutableStateOf<AdminClient?>(null) }
    var draftPlanId by remember { mutableStateOf<String?>(null) }
    // Cortesía = plan de cortesía (sin cobro): se deriva del plan del borrador, sin casilla.
    val draftCourtesy = plans.firstOrNull { it.id == draftPlanId }?.billingPeriod ==
        AdminBillingPeriod.COURTESY
    var draftTrainerId by remember { mutableStateOf<String?>(null) }
    var draftPaidAt by remember { mutableStateOf(todayIsoDate()) }
    var draftStartsAt by remember { mutableStateOf(todayIsoDate()) }
    var draftEndsAt by remember { mutableStateOf("") }
    var draftWalletApplyCents by remember { mutableStateOf(0L) }
    /** true cuando la tarjeta viene de «Editar» de la lista, para reetiquetar el botón. */
    var draftEditing by remember { mutableStateOf(false) }
    var mode by remember { mutableStateOf(BulkPaymentMode.PerClient) }
    /** Fila de la cola cuyo cobro se está configurando. */
    var paymentRowIndex by remember { mutableStateOf<Int?>(null) }
    var draftPaymentDialogOpen by remember { mutableStateOf(false) }
    var draftPaymentLines by remember { mutableStateOf<List<PaymentLineDraft>>(emptyList()) }
    var draftDiscount by remember { mutableStateOf<AdminPaymentDiscount?>(null) }
    var draftChangeGivenCents by remember { mutableStateOf(0L) }
    var draftPendingUsdCents by remember { mutableStateOf(0L) }
    var draftPendingBsCents by remember { mutableStateOf(0L) }
    var draftPaymentConfigured by remember { mutableStateOf(false) }
    val queue = remember { mutableStateListOf<BulkPaymentQueueItem>() }
    var sharedPaymentLines by remember { mutableStateOf<List<PaymentLineDraft>>(emptyList()) }
    /** Total para el que se escribieron las líneas; si cambia (monedero, plan) hay que reajustarlas. */
    var sharedLinesTotal by remember { mutableStateOf(-1L) }
    var sharedChangeGivenCents by remember { mutableStateOf(0L) }
    var sharedPendingUsdInput by remember { mutableStateOf(AMOUNT_INPUT_ZERO) }
    var paymentConfigured by remember { mutableStateOf(false) }
    var registerWithoutPayment by remember { mutableStateOf(false) }
    var sharedPaymentError by remember { mutableStateOf<String?>(null) }
    var processResults by remember { mutableStateOf<List<BulkProcessResultItem>?>(null) }
    var processSummary by remember { mutableStateOf("") }
    var exchangeQuote by remember { mutableStateOf<AdminExchangeRateQuote?>(null) }
    var walletModuleEnabled by remember { mutableStateOf(false) }
    var bulkPaymentsAlwaysPaid by remember { mutableStateOf(false) }
    /** Aviso local (p. ej. plan/periodo duplicado) junto al formulario de agregar. */
    var draftError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        exchangeQuote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
        val modules = runCatching { AdminRepo.api.getSettingsModules() }.getOrNull()
        walletModuleEnabled = modules?.clientAccountsEnabled ?: false
        bulkPaymentsAlwaysPaid = modules?.bulkPaymentsAlwaysPaid ?: false
    }

    val defaultPlanId = remember(plans) {
        plans.firstOrNull { it.name.trim().equals("semanal", ignoreCase = true) }?.id
            ?: plans.firstOrNull { it.name.contains("semanal", ignoreCase = true) }?.id
            ?: plans.firstOrNull()?.id
    }

    LaunchedEffect(defaultPlanId) {
        if (draftPlanId == null) draftPlanId = defaultPlanId
    }

    // Validar plan+periodo al elegir socio / plan / fecha (antes de agregar a la lista).
    LaunchedEffect(selected?.id, draftPlanId, draftStartsAt) {
        val client = selected
        if (client == null) {
            draftError = null
            return@LaunchedEffect
        }
        val planId = draftPlanId
        if (planId.isNullOrBlank()) {
            draftError = null
            return@LaunchedEffect
        }
        val plan = plans.firstOrNull { it.id == planId }
        val startsAt = parseFlexibleDateToIso(draftStartsAt.trim())
            ?: draftStartsAt.trim().ifBlank { todayIsoDate() }
        val covered = runCatching {
            AdminRepo.api.isMembershipPeriodCovered(
                clientId = client.id,
                planId = planId,
                orderDate = startsAt.take(10),
            )
        }.getOrDefault(false)
        draftError = if (covered) {
            if (plan != null) {
                duplicateMembershipPeriodMessage(plan.name, plan.billingPeriod)
            } else {
                "Plan/periodo duplicado: este socio ya tiene ese plan para el mismo periodo."
            }
        } else {
            null
        }
    }

    val draftAmountCents = remember(draftPlanId, plans) {
        plans.firstOrNull { it.id == draftPlanId }?.priceCents ?: 0L
    }

    val chargeableItems = remember(queue.toList(), walletModuleEnabled) {
        queue.filter { !it.isCourtesy }.map { item ->
            if (walletModuleEnabled) item
            else item.copy(walletApplyCents = 0L)
        }
    }
    val grossChargeableTotal = chargeableItems.sumOf { it.amountCents }
    val walletAppliedTotal = chargeableItems.sumOf { it.walletApplyCents.coerceAtMost(it.amountCents) }
    val chargeableTotal = (grossChargeableTotal - walletAppliedTotal).coerceAtLeast(0L)
    val chargeableUsdTotal = remember(chargeableItems, plans) {
        chargeableItems.sumOf { item ->
            plans.firstOrNull { it.id == item.planId }?.priceUsdCents?.coerceAtLeast(0L) ?: 0L
        }
    }
    val chargeableDualLabel = remember(chargeableTotal, chargeableUsdTotal) {
        buildList {
            if (chargeableTotal > 0) add(formatMoney(chargeableTotal.toInt(), GymlyCurrency.CODE))
            if (chargeableUsdTotal > 0) add(formatMoney(chargeableUsdTotal.toInt(), "USD"))
        }.joinToString(" · ").ifBlank { formatMoney(0, GymlyCurrency.CODE) }
    }
    val chargeableTotalInput = if (chargeableTotal <= 0) "" else (chargeableTotal / 100.0).toString()
    val chargeableCount = chargeableItems.size

    // Modo por socio: cuántos faltan por resolver y cuánto suman los cobros ya cargados.
    val perClientPendingCount = chargeableItems.count { !it.settled }
    val perClientPaidLabel = remember(chargeableItems) {
        val allLines = chargeableItems.flatMap { it.paymentLines }
        if (allLines.isEmpty()) null else paymentLinesPaidTotalsLabel(allLines, "")
    }

    val checkoutUsdDefault = remember(
        chargeableItems,
        chargeableTotal,
        plans,
        exchangeQuote,
    ) {
        checkoutUsdDefaultAmount(chargeableItems, plans, chargeableTotal, exchangeQuote, useFullPlanAmount = false)
    }

    val hits = remember(clients, query) {
        val q = query.trim()
        if (q.length < 2) {
            emptyList()
        } else {
            filterClientsForPickerSearch(clients.forClientPicker(), query, limit = 8)
        }
    }

    LaunchedEffect(mode, chargeableTotal, walletAppliedTotal, grossChargeableTotal, walletModuleEnabled) {
        if (mode != BulkPaymentMode.Shared) {
            sharedPaymentLines = emptyList()
            sharedLinesTotal = -1L
            sharedChangeGivenCents = 0L
            paymentConfigured = false
        } else if (grossChargeableTotal <= 0) {
            sharedPaymentLines = emptyList()
            sharedLinesTotal = -1L
            sharedChangeGivenCents = 0L
            paymentConfigured = false
            registerWithoutPayment = false
        } else if (walletModuleEnabled && chargeableTotal <= 0) {
            // Cubierto por monedero: no hace falta método de pago externo.
            sharedPaymentLines = emptyList()
            sharedLinesTotal = -1L
            sharedChangeGivenCents = 0L
            paymentConfigured = true
            registerWithoutPayment = false
        } else if (sharedPaymentLines.isEmpty() && !registerWithoutPayment) {
            sharedPaymentLines = listOf(PaymentLineDraft(amountInput = chargeableTotalInput))
            sharedLinesTotal = chargeableTotal
            sharedChangeGivenCents = 0L
        }
    }

    // Al usar monedero o cambiar el plan, la última línea en Bs absorbe la nueva diferencia.
    val checkoutLines = if (sharedLinesTotal == chargeableTotal) {
        sharedPaymentLines
    } else {
        rescalePaymentLinesToTotal(sharedPaymentLines, chargeableTotal)
    }

    val paymentSummary = remember(checkoutLines, paymentConfigured, chargeableTotalInput, walletAppliedTotal) {
        when {
            !paymentConfigured || grossChargeableTotal <= 0 -> null
            chargeableTotal <= 0 && walletAppliedTotal > 0 ->
                "Monedero digital ${formatMoney(walletAppliedTotal.toInt(), GymlyCurrency.CODE)}"
            else -> {
                val linesLabel = paymentLinesSummaryLabel(checkoutLines, chargeableTotalInput)
                val paid = paymentLinesPaidTotalsLabel(checkoutLines, chargeableTotalInput)
                val base = if (paid != "—" && paid.isNotBlank()) "$linesLabel · $paid" else linesLabel
                if (walletAppliedTotal > 0) {
                    "Monedero ${formatMoney(walletAppliedTotal.toInt(), GymlyCurrency.CODE)} + $base"
                } else {
                    base
                }
            }
        }
    }

    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
        OutlinedTextField(
            value = query,
            onValueChange = {
                query = it
                if (selected != null) selected = null
            },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            placeholder = {
                Text(
                    if (selected == null) {
                        "Buscar socio (solo clientes activos) por nombre, cédula o correo…"
                    } else {
                        "Buscar otro socio…"
                    },
                    color = AdminColors.TextMuted,
                )
            },
            shape = RoundedCornerShape(12.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )

        if (selected == null) {
            when {
                query.trim().isEmpty() -> {
                    Text(
                        "Escribe al menos 2 caracteres para buscar un socio.",
                        color = AdminColors.TextMuted,
                        fontSize = 13.sp,
                    )
                }
                query.trim().length >= 2 && hits.isEmpty() -> {
                    Text("No hay socio con ese dato.", color = AdminColors.TextMuted, fontSize = 13.sp)
                }
            }
            hits.forEach { client ->
                BulkPaymentSearchHit(
                    client = client,
                    onSelect = {
                        selected = client
                        // Otro socio: el pendiente del anterior no debe quedar en su cobro.
                        draftPendingBsCents = 0L
                        draftPendingUsdCents = 0L
                        query = client.fullName
                        draftPlanId = draftPlanId ?: defaultPlanId
                        draftPaidAt = todayIsoDate()
                        draftStartsAt = todayIsoDate()
                        draftEndsAt = (draftPlanId ?: defaultPlanId)?.let { id ->
                            plans.firstOrNull { it.id == id }?.let {
                                planEndsAtFromStart(todayIsoDate(), it.billingPeriod)
                            }
                        }.orEmpty()
                        draftWalletApplyCents = 0L
                        draftPaymentLines = emptyList()
                        draftChangeGivenCents = 0L
                        draftPaymentConfigured = false
                    },
                )
            }
        }

        selected?.let { client ->
            BulkPaymentAddMemberCard(
                client = client,
                plans = plans,
                trainers = trainers,
                planId = draftPlanId,
                trainerId = draftTrainerId,
                paidAt = draftPaidAt,
                startsAt = draftStartsAt,
                endsAt = draftEndsAt,
                amountCents = draftAmountCents,
                isCourtesy = draftCourtesy,
                editing = draftEditing,
                processing = processing,
                showPaymentStep = mode == BulkPaymentMode.PerClient,
                paymentConfigured = draftPaymentConfigured,
                paymentSummary = bulkPaymentRowSummary(
                    draftPaymentLines,
                    draftWalletApplyCents,
                    centsToAmountInput((draftAmountCents - draftWalletApplyCents).coerceAtLeast(0L)),
                ),
                paymentBreakdown = buildBulkPaymentBreakdown(
                    lines = draftPaymentLines,
                    walletApplyCents = draftWalletApplyCents,
                    discountBsCents = draftDiscount?.cents ?: 0L,
                    discountUsdCents = draftDiscount?.usdCents ?: 0L,
                    changeBsCents = draftChangeGivenCents,
                    changeUsdCents = 0L,
                    pendingBsCents = draftPendingBsCents,
                    pendingUsdCents = draftPendingUsdCents,
                    dueBsCents = (draftAmountCents - draftWalletApplyCents).coerceAtLeast(0L),
                ),
                walletApplyCents = draftWalletApplyCents,
                onPlan = {
                    draftPlanId = it
                    val start = draftStartsAt.ifBlank { todayIsoDate() }
                    draftEndsAt = it?.let { id ->
                        plans.firstOrNull { p -> p.id == id }?.let { plan ->
                            planEndsAtFromStart(start, plan.billingPeriod)
                        }
                    }.orEmpty()
                    draftWalletApplyCents = 0L
                    draftPaymentLines = emptyList()
                    draftChangeGivenCents = 0L
                    draftPaymentConfigured = false
                },
                onTrainer = { draftTrainerId = it },
                onPaidAt = { draftPaidAt = it },
                onStartsAt = { newStart ->
                    draftStartsAt = newStart
                    val start = newStart.ifBlank { todayIsoDate() }
                    draftEndsAt = draftPlanId?.let { id ->
                        plans.firstOrNull { it.id == id }?.let { plan ->
                            planEndsAtFromStart(start, plan.billingPeriod)
                        }
                    }.orEmpty()
                },
                onEndsAt = { draftEndsAt = it },
                onConfigurePayment = { draftPaymentDialogOpen = true },
                onClearPayment = {
                    draftPaymentLines = emptyList()
                    draftChangeGivenCents = 0L
                    draftPaymentConfigured = false
                    draftWalletApplyCents = 0L
                },
                onClear = {
                    selected = null
                    query = ""
                    draftWalletApplyCents = 0L
                    draftEditing = false
                    draftPaymentLines = emptyList()
                    draftChangeGivenCents = 0L
                    draftPaymentConfigured = false
                    draftError = null
                },
                draftError = draftError,
                onAdd = {
                    if (!draftError.isNullOrBlank() && draftError!!.contains("duplicado", ignoreCase = true)) {
                        onError(draftError!!)
                        return@BulkPaymentAddMemberCard
                    }
                    val planId = draftPlanId
                    if (planId.isNullOrBlank()) {
                        draftError = "Selecciona un plan para este socio."
                        onError(draftError!!)
                        return@BulkPaymentAddMemberCard
                    }
                    val plan = plans.firstOrNull { it.id == planId }
                    val planName = plan?.name ?: "Plan"
                    val billingPeriod = plan?.billingPeriod
                    val startsAt = parseFlexibleDateToIso(draftStartsAt.trim())
                        ?: draftStartsAt.trim().ifBlank { todayIsoDate() }
                    val periodKey = billingPeriod?.let { membershipPeriodKey(it, startsAt) }
                    val duplicateInQueue = periodKey != null && queue.any { item ->
                        if (item.ok == true || item.client.id != client.id || item.planId != planId) {
                            false
                        } else {
                            val itemPeriod = plans.firstOrNull { it.id == item.planId }?.billingPeriod
                            itemPeriod != null && membershipPeriodKey(itemPeriod, item.startsAt) == periodKey
                        }
                    }
                    if (duplicateInQueue && billingPeriod != null) {
                        val msg = duplicateMembershipPeriodMessage(planName, billingPeriod) +
                            " Ya está en esta lista."
                        draftError = msg
                        onError(msg)
                        return@BulkPaymentAddMemberCard
                    }
                    val trainerName = trainers.firstOrNull { it.id == draftTrainerId }?.displayName
                        ?: "Sin entrenador"
                    val cents = if (draftCourtesy) {
                        0L
                    } else {
                        plan?.priceCents ?: 0L
                    }
                    if (!draftCourtesy && cents <= 0) {
                        draftError = "El plan seleccionado no tiene monto válido."
                        onError(draftError!!)
                        return@BulkPaymentAddMemberCard
                    }
                    draftError = null
                    val clientSnap = client
                    val courtesySnap = draftCourtesy
                    val trainerSnap = draftTrainerId
                    val walletSnap = draftWalletApplyCents
                    val linesSnap = draftPaymentLines
                    val discountSnap = draftDiscount
                    val changeSnap = draftChangeGivenCents
                    val pendingUsdSnap = draftPendingUsdCents
                    val pendingBsSnap = draftPendingBsCents
                    val paymentConfiguredSnap = draftPaymentConfigured
                    scope.launch {
                        val covered = runCatching {
                            AdminRepo.api.isMembershipPeriodCovered(
                                clientId = clientSnap.id,
                                planId = planId,
                                orderDate = startsAt.take(10),
                            )
                        }.getOrDefault(false)
                        if (covered) {
                            val msg = if (billingPeriod != null) {
                                duplicateMembershipPeriodMessage(planName, billingPeriod)
                            } else {
                                "Plan/periodo duplicado: este socio ya tiene ese plan para el mismo periodo. " +
                                    "No se puede cobrar dos veces."
                            }
                            draftError = msg
                            onError(msg)
                            return@launch
                        }
                        queue.add(
                            BulkPaymentQueueItem(
                                client = clientSnap,
                                planId = planId,
                                planLabel = planName,
                                trainerId = trainerSnap,
                                trainerLabel = trainerName,
                                paidAt = draftPaidAt.ifBlank { todayIsoDate() },
                                startsAt = startsAt,
                                endsAt = draftEndsAt.ifBlank {
                                    plan?.let { planEndsAtFromStart(startsAt, it.billingPeriod) }.orEmpty()
                                },
                                isCourtesy = courtesySnap,
                                amountCents = cents,
                                walletApplyCents = if (courtesySnap || !walletModuleEnabled) {
                                    0L
                                } else {
                                    walletSnap.coerceAtMost(cents)
                                },
                                paymentLines = if (courtesySnap) emptyList() else linesSnap,
                                changeGivenCents = if (courtesySnap) 0L else changeSnap,
                                pendingUsdCents = if (courtesySnap) 0L else pendingUsdSnap,
                                pendingBsCents = if (courtesySnap) 0L else pendingBsSnap,
                                paymentConfigured = !courtesySnap && paymentConfiguredSnap,
                                discount = if (courtesySnap) null else discountSnap,
                            ),
                        )
                        paymentConfigured = false
                        sharedPaymentError = null
                        draftError = null
                        selected = null
                        query = ""
                        draftTrainerId = null
                        draftPaidAt = todayIsoDate()
                        draftStartsAt = todayIsoDate()
                        draftEndsAt = draftPlanId?.let { id ->
                            plans.firstOrNull { it.id == id }?.let {
                                planEndsAtFromStart(todayIsoDate(), it.billingPeriod)
                            }
                        }.orEmpty()
                        draftWalletApplyCents = 0L
                        draftEditing = false
                        draftPaymentLines = emptyList()
                        draftDiscount = null
                        draftChangeGivenCents = 0L
                        draftPendingUsdCents = 0L
                        draftPendingBsCents = 0L
                        draftPaymentConfigured = false
                    }
                },
            )
        }

        if (queue.isNotEmpty()) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(16.dp),
            ) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        "Lista a cobrar (${queue.size})",
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        color = AdminColors.Text,
                    )
                    Text(
                        formatMoney(queue.sumOf { it.amountCents }.toInt(), GymlyCurrency.CODE),
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        color = AdminColors.Primary,
                    )
                }
                Spacer(Modifier.height(12.dp))
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    queue.forEachIndexed { index, item ->
                        BulkPaymentQueueRow(
                            item = item,
                            plans = plans,
                            processing = processing,
                            perClientPayment = mode == BulkPaymentMode.PerClient,
                            paymentSummary = bulkPaymentRowSummary(
                                item.paymentLines,
                                item.walletApplyCents,
                                centsToAmountInput(item.remainingCents),
                            ),
                            onRemove = {
                                queue.removeAt(index)
                                paymentConfigured = false
                                registerWithoutPayment = false
                                sharedPaymentError = null
                            },
                            onEdit = {
                                queue.removeAt(index)
                                selected = item.client
                                query = item.client.fullName
                                draftPlanId = item.planId
                                draftTrainerId = item.trainerId
                                draftPaidAt = item.paidAt.ifBlank { todayIsoDate() }
                                draftStartsAt = item.startsAt
                                draftEndsAt = item.endsAt
                                draftWalletApplyCents = item.walletApplyCents
                                draftPaymentLines = item.paymentLines
                                draftDiscount = item.discount
                                // Al editar un socio de la cola, su pendiente guardado vuelve al cobro.
                                draftPendingBsCents = item.pendingBsCents
                                draftPendingUsdCents = item.pendingUsdCents
                                draftChangeGivenCents = item.changeGivenCents
                                draftPaymentConfigured = item.paymentConfigured
                                draftEditing = true
                                paymentConfigured = false
                                registerWithoutPayment = false
                                sharedPaymentError = null
                            },
                            onConfigurePayment = { paymentRowIndex = index },
                            onClearPayment = {
                                queue[index] = item.copy(
                                    paymentLines = emptyList(),
                                    changeGivenCents = 0L,
                                    paymentConfigured = false,
                                    walletApplyCents = 0L,
                                )
                                sharedPaymentError = null
                            },
                            onWalletApply = { applied ->
                                val newTotal = (item.walletApplyCents + applied).coerceAtMost(item.amountCents)
                                queue[index] = item.copy(walletApplyCents = newTotal)
                                sharedPaymentError = null
                            },
                        )
                    }
                }
            }
        } else {
            BulkPaymentEmptyQueueHint()
        }

        BulkPaymentCheckoutSection(
            mode = mode,
            queueSize = queue.size,
            chargeableCount = chargeableCount,
            grossChargeableTotal = grossChargeableTotal,
            chargeableTotal = chargeableTotal,
            chargeableDualLabel = chargeableDualLabel,
            walletAppliedTotal = walletAppliedTotal,
            walletModuleEnabled = walletModuleEnabled,
            chargeableTotalInput = chargeableTotalInput,
            defaultUsdAmount = checkoutUsdDefault,
            perClientPendingCount = perClientPendingCount,
            perClientPaidLabel = perClientPaidLabel,
            paymentConfigured = paymentConfigured,
            registerWithoutPayment = registerWithoutPayment,
            paymentSummary = paymentSummary,
            paymentError = sharedPaymentError,
            processing = processing,
            initialPaymentLines = checkoutLines,
            pendingUsdInput = sharedPendingUsdInput,
            onPendingUsdChange = { sharedPendingUsdInput = it },
            onModeChange = {
                mode = it
                sharedPaymentError = null
            },
            onRegisterWithoutPaymentChange = { registerWithoutPayment = it },
            onLinesChange = { lines ->
                sharedPaymentLines = lines
                sharedLinesTotal = chargeableTotal
                paymentConfigured = (lines.isNotEmpty() && chargeableTotal > 0) ||
                    (walletModuleEnabled && chargeableTotal <= 0 && walletAppliedTotal > 0)
            },
            onChangeGivenCentsChange = { sharedChangeGivenCents = it },
            onRegister = {
                    if (processing || queue.isEmpty()) return@BulkPaymentCheckoutSection
                    // Modo compartido: el pendiente en $ se escribe siempre (0,00 si no queda saldo).
                    if (mode != BulkPaymentMode.PerClient && sharedPendingUsdInput.isBlank()) {
                        sharedPaymentError = "Escribe el pendiente en \$ (0,00 si no queda saldo)."
                        return@BulkPaymentCheckoutSection
                    }
                    val chargeable = queue.filter { !it.isCourtesy }

                    // Excedente en Bs sin vuelto declarado: se revisa ANTES de crear órdenes o cobros.
                    // Mismo criterio que Ventas y Órdenes. Las líneas en divisas no se comparan.
                    fun undeclaredBsExcess(lines: List<PaymentLineDraft>, dueCents: Long, changeCents: Long): Long {
                        val bs = lines.filterNot { it.method.usesUsdAmount() }
                            .sumOf { parseBulkAmountToCents(it.amountInput) }
                        return excedenteBsSinVuelto(bs, dueCents, changeCents)
                    }
                    if (!registerWithoutPayment) {
                        if (mode == BulkPaymentMode.PerClient) {
                            for (item in chargeable) {
                                // El vuelto que declaró este socio en su diálogo: si no se pasa, el excedente cuenta como error.
                                val undeclared = undeclaredBsExcess(item.paymentLines, item.remainingCents, item.changeGivenCents)
                                if (undeclared > maxOf(100L, item.remainingCents / 200L)) {
                                    sharedPaymentError = "${item.client.fullName}: el cobro supera lo debido en Bs " +
                                        formatMoney(undeclared.toInt(), GymlyCurrency.CODE) +
                                        " y no tiene vuelto declarado. Corrige el monto."
                                    return@BulkPaymentCheckoutSection
                                }
                            }
                        } else {
                            val undeclared = undeclaredBsExcess(sharedPaymentLines, chargeableTotal, sharedChangeGivenCents)
                            if (undeclared > maxOf(100L, chargeableTotal / 200L)) {
                                sharedPaymentError = "El cobro supera lo debido en Bs " +
                                    formatMoney(undeclared.toInt(), GymlyCurrency.CODE) +
                                    " y no tiene vuelto declarado. Corrige el monto o declara el vuelto."
                                return@BulkPaymentCheckoutSection
                            }
                        }
                    }

                    // Modo por socio: cada uno se cobra con sus propias líneas.
                    if (mode == BulkPaymentMode.PerClient && !registerWithoutPayment) {
                        val notSettled = chargeable.filter { !it.settled }
                        if (notSettled.isNotEmpty()) {
                            sharedPaymentError = "Falta configurar el cobro de: " +
                                notSettled.joinToString(", ") { it.client.fullName }
                            return@BulkPaymentCheckoutSection
                        }
                        for (item in chargeable) {
                            for (line in item.paymentLines) {
                                paymentReferenceValidationError(line)?.let {
                                    sharedPaymentError = "${item.client.fullName}: $it"
                                    return@BulkPaymentCheckoutSection
                                }
                            }
                        }
                        val sharedMobileTips = bulkSharedMobileRefAlerts(
                            chargeable.map { it.client.fullName to it.paymentLines },
                        )
                        sharedPaymentError = null
                        scope.launch {
                            onProcessingChange(true)
                            onProgress(0, queue.size)
                            var okCount = 0
                            var failCount = 0
                            var walletApplyNotes = 0
                            var walletNotes = 0
                            var excessTotal = 0L
                            try {
                                val quote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                                queue.forEachIndexed { index, item ->
                                    try {
                                        val periodCovered = runCatching {
                                            AdminRepo.api.isMembershipPeriodCovered(
                                                clientId = item.client.id,
                                                planId = item.planId,
                                                orderDate = item.startsAt.take(10),
                                            )
                                        }.getOrDefault(false)
                                        if (periodCovered) {
                                            val period = plans.firstOrNull { it.id == item.planId }?.billingPeriod
                                            queue[index] = item.copy(
                                                ok = false,
                                                result = if (period != null) {
                                                    duplicateMembershipPeriodMessage(item.planLabel, period)
                                                } else {
                                                    "Periodo ya cubierto · no se crea orden duplicada"
                                                },
                                                orderId = null,
                                            )
                                            failCount++
                                            onProgress(index + 1, queue.size)
                                            return@forEachIndexed
                                        }

                                        if (item.isCourtesy) {
                                            val order = AdminRepo.api.createMembershipOrder(
                                                AdminMembershipOrderUpsert(
                                                    clientId = item.client.id,
                                                    planId = item.planId,
                                                    branchId = companyId,
                                                    trainerId = item.trainerId,
                                                    method = null,
                                                    status = AdminMembershipOrderStatus.COURTESY,
                                                    orderDate = item.startsAt,
                                                    amountCents = item.amountCents,
                                                    currency = GymlyCurrency.CODE,
                                                    notes = "Alta masiva · cortesía · ${item.planLabel}",
                                                ),
                                            )
                                            queue[index] = item.copy(
                                                ok = true,
                                                result = "orden ${order.displayNumber()} · cortesía",
                                                orderId = order.id,
                                            )
                                            okCount++
                                            onProgress(index + 1, queue.size)
                                            return@forEachIndexed
                                        }

                                        val itemDueInput = centsToAmountInput(item.remainingCents)
                                        // Cobro que viaja con la creación: si la alta queda en aprobación, se aplica al aprobarla.
                                        // Si la alta es directa, el servidor lo ignora y el cobro se registra abajo.
                                        val createRemaining = item.remainingCents
                                        val createHasUsdPay = item.paymentLines.any { line ->
                                            line.method.usesUsdAmount() &&
                                                parseBulkAmountToCents(line.amountInput) > 0
                                        }
                                        val inlineCollect = if ((createRemaining > 0 || createHasUsdPay) && item.paymentLines.isNotEmpty()) {
                                            val inlineDueInput = centsToAmountInput(
                                                createRemaining.coerceAtLeast(item.amountCents - item.walletApplyCents),
                                            )
                                            val (inlineMerged, inlineLinesBase) = prepareBulkCollectLines(
                                                entries = listOf(inlineDueInput to item.paymentLines),
                                                fallbackAmountInput = inlineDueInput,
                                                quote = quote,
                                            )
                                            // Comprobantes guardados ya: se asocian al pago si la alta queda en aprobación.
                                            val inlineLines = inlineLinesBase.mapIndexed { i, line ->
                                                val proof = inlineMerged.getOrNull(i)?.proof
                                                if (proof == null) line else line.copy(proofPath = AdminRepo.api.stageMembershipOrderProof(proof))
                                            }
                                            AdminPaymentCollect(
                                                currency = GymlyCurrency.CODE,
                                                targets = listOf(
                                                    AdminPaymentCollectTarget(
                                                        clientId = item.client.id,
                                                        amountCents = createRemaining,
                                                        planId = item.planId,
                                                        branchId = companyId,
                                                        trainerId = item.trainerId,
                                                        discount = item.discount,
                                                    ),
                                                ),
                                                lines = inlineLines,
                                                paidAt = paymentDateToPaidAtIso(item.paidAt),
                                                changeGivenCents = item.changeGivenCents,
                                                changeGivenUsdCents = item.changeGivenUsdCents,
                                            )
                                        } else {
                                            null
                                        }

                                        val order = AdminRepo.api.createMembershipOrder(
                                            AdminMembershipOrderUpsert(
                                                clientId = item.client.id,
                                                planId = item.planId,
                                                branchId = companyId,
                                                trainerId = item.trainerId,
                                                method = item.paymentLines.firstOrNull()?.method,
                                                status = if (bulkPaymentsAlwaysPaid || item.remainingCents <= 0 || item.discount != null) AdminMembershipOrderStatus.PAID else AdminMembershipOrderStatus.PENDING,
                                                orderDate = item.startsAt,
                                                amountCents = item.amountCents,
                                                pendingUsdCents = item.pendingUsdCents,
                                                pendingBsCents = item.pendingBsCents,
                                                currency = GymlyCurrency.CODE,
                                                notes = "Alta masiva · ${item.planLabel}",
                                                collect = inlineCollect,
                                            ),
                                        )
                                        var walletNote = ""
                                        if (walletModuleEnabled && item.walletApplyCents > 0) {
                                            AdminRepo.api.applyClientAccountCredit(
                                                item.client.id,
                                                AdminClientAccountApplyCredit(
                                                    membershipOrderId = order.id,
                                                    amountCents = item.walletApplyCents,
                                                ),
                                            )
                                            walletApplyNotes++
                                            walletNote = " · monedero " +
                                                formatMoney(item.walletApplyCents.toInt(), GymlyCurrency.CODE)
                                        }

                                        val remaining = item.remainingCents
                                        var paidNote = ""
                                        val hasUsdPay = item.paymentLines.any { line ->
                                            line.method.usesUsdAmount() &&
                                                parseBulkAmountToCents(line.amountInput) > 0
                                        }
                                        if ((remaining > 0 || hasUsdPay) && item.paymentLines.isNotEmpty()) {
                                            val itemDueInput = centsToAmountInput(
                                                remaining.coerceAtLeast(item.amountCents - item.walletApplyCents),
                                            )
                                            val (_, collectLines) = prepareBulkCollectLines(
                                                entries = listOf(itemDueInput to item.paymentLines),
                                                fallbackAmountInput = itemDueInput,
                                                quote = quote,
                                            )
                                            validateBulkCollectCoverage(
                                                dueBsTotal = remaining,
                                                collectLines = collectLines,
                                            )?.let { msg ->
                                                throw IllegalStateException("${item.client.fullName}: $msg")
                                            }
                                            val payments = AdminRepo.api.collectPayments(
                                                AdminPaymentCollect(
                                                    currency = GymlyCurrency.CODE,
                                                    targets = listOf(
                                                        AdminPaymentCollectTarget(
                                                            clientId = item.client.id,
                                                            membershipOrderId = order.id,
                                                            amountCents = remaining,
                                                            planId = item.planId,
                                                            branchId = companyId,
                                                            trainerId = item.trainerId,
                                                            discount = item.discount,
                                                        ),
                                                    ),
                                                    lines = collectLines,
                                                    paidAt = paymentDateToPaidAtIso(item.paidAt),
                                                    changeGivenCents = item.changeGivenCents,
                                                    changeGivenUsdCents = item.changeGivenUsdCents,
                                                ),
                                            )
                                            item.paymentLines.forEachIndexed { lineIndex, line ->
                                                line.proof?.let { proof ->
                                                    payments.getOrNull(lineIndex)?.let { payment ->
                                                        AdminRepo.api.uploadPaymentProof(payment.id, proof)
                                                    }
                                                }
                                            }
                                            val paidBs = collectLines.sumOf { it.amountCents }.coerceAtLeast(0L)
                                            val hasUsd = collectLines.any { (it.foreignUsdCents ?: 0L) > 0L }
                                            // Vuelto en Bs puede venir de excedente en $; tope solo si hubo Bs.
                                            val change = when {
                                                paidBs > 0L -> item.changeGivenCents.coerceIn(0L, paidBs)
                                                hasUsd -> item.changeGivenCents.coerceAtLeast(0L)
                                                else -> 0L
                                            }
                                            val overpay = (paidBs - remaining).coerceAtLeast(0L)
                                            val walletCredit = if (walletModuleEnabled) {
                                                (overpay - change).coerceAtLeast(0L)
                                            } else {
                                                0L
                                            }
                                            if (walletCredit > 0) {
                                                excessTotal += walletCredit
                                            }
                                            paidNote = overpayDispositionNote(change, walletCredit)
                                        }

                                        queue[index] = item.copy(
                                            ok = true,
                                            result = "orden ${order.displayNumber()} · " +
                                                paymentLinesSummaryLabel(item.paymentLines, itemDueInput) +
                                                walletNote + paidNote,
                                            orderId = order.id,
                                        )
                                        okCount++
                                    } catch (e: Throwable) {
                                        val pending = pendingApprovals.offer(e)
                                        queue[index] = item.copy(
                                            ok = pending,
                                            result = if (pending) "Enviada a aprobación" else e.userMessage("Error"),
                                        )
                                        if (pending) okCount++ else failCount++
                                    }
                                    onProgress(index + 1, queue.size)
                                }

                                val dupCount = queue.count {
                                    it.ok == false && it.result.orEmpty().contains("duplicado", ignoreCase = true)
                                }
                                val dupHint = if (dupCount > 0) {
                                    " · $dupCount plan/periodo duplicado(s)"
                                } else {
                                    ""
                                }
                                onDoneMessage(
                                    "Cobros: $okCount ok · $failCount con error$dupHint" +
                                        (if (walletApplyNotes > 0) " · $walletApplyNotes con monedero" else "") +
                                        (if (walletNotes > 0) " · $walletNotes abonos monedero" else "") +
                                        (
                                            if (excessTotal > 0) {
                                                " · excedente " +
                                                    formatMoney(excessTotal.toInt(), GymlyCurrency.CODE) + " → monedero"
                                            } else {
                                                ""
                                            }
                                            ) +
                                        (
                                            if (sharedMobileTips.isNotEmpty()) {
                                                " · " + sharedMobileTips.joinToString(" · ")
                                            } else {
                                                ""
                                            }
                                            ),
                                )
                                processSummary = "$okCount correctos · $failCount con error$dupHint" +
                                    (if (walletApplyNotes > 0) " · $walletApplyNotes monedero aplicado" else "")
                                if (dupCount > 0) {
                                    onError(
                                        "Hay $dupCount socio(s) con plan/periodo duplicado. " +
                                            "Revisa el detalle: no se puede cobrar el mismo plan dos veces en el mismo periodo.",
                                    )
                                }
                                processResults = queue.map { entry ->
                                    BulkProcessResultItem(
                                        title = entry.client.fullName,
                                        subtitle = entry.result,
                                        ok = entry.ok == true,
                                    )
                                }
                            } catch (e: Throwable) {
                                onError(e.userMessage("Error al registrar cobro"))
                            } finally {
                                onProcessingChange(false)
                            }
                        }
                        return@BulkPaymentCheckoutSection
                    }

                    val skipPayment = registerWithoutPayment && chargeable.isNotEmpty()
                    if (skipPayment && chargeable.any { it.discount != null }) {
                        // Sin cobro no hay a qué aplicar el descuento: la orden quedaría pagada sin pago.
                        sharedPaymentError = "Hay descuentos cargados: cobra la planilla o quita los descuentos."
                        return@BulkPaymentCheckoutSection
                    }
                    val remainingDue = chargeable.sumOf { it.remainingCents }
                    val lines = checkoutLines.ifEmpty {
                        if (remainingDue > 0) listOf(PaymentLineDraft(amountInput = chargeableTotalInput))
                        else emptyList()
                    }
                    if (chargeable.isNotEmpty() && !skipPayment && remainingDue > 0) {
                        for (line in lines) {
                            paymentReferenceValidationError(line)?.let {
                                sharedPaymentError = it
                                return@BulkPaymentCheckoutSection
                            }
                        }
                    }
                    if (chargeable.isNotEmpty() && !skipPayment && remainingDue > 0 && lines.isEmpty()) {
                        sharedPaymentError = "Indica al menos un método de pago."
                        return@BulkPaymentCheckoutSection
                    }
                    sharedPaymentError = null
                    val primaryMethod = if (skipPayment || remainingDue <= 0) null else lines.firstOrNull()?.method
                    scope.launch {
                        onProcessingChange(true)
                        onProgress(0, queue.size)
                        var okCount = 0
                        var failCount = 0
                        var walletApplyNotes = 0
                        try {
                            val exchangeQuote = if (!skipPayment && remainingDue > 0) {
                                runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                            } else {
                                null
                            }
                            if (chargeable.isNotEmpty() && !skipPayment && remainingDue > 0) {
                                for (line in lines) {
                                    if (!paymentLineHasPositiveAmount(line, chargeableTotalInput, exchangeQuote)) {
                                        sharedPaymentError = if (line.method.usesUsdAmount()) {
                                            "Cada línea en dólares debe tener un monto USD válido."
                                        } else {
                                            "Cada línea de cobro debe tener un monto válido."
                                        }
                                        onProcessingChange(false)
                                        return@launch
                                    }
                                }
                            }

                            val collectTargets = mutableListOf<AdminPaymentCollectTarget>()
                            val walletDeposits = mutableListOf<AdminClientWalletDepositItem>()
                            var walletNotes = 0

                            queue.forEachIndexed { index, item ->
                                try {
                                    val periodCovered = runCatching {
                                        AdminRepo.api.isMembershipPeriodCovered(
                                            clientId = item.client.id,
                                            planId = item.planId,
                                            orderDate = item.startsAt.take(10),
                                        )
                                    }.getOrDefault(false)
                                    if (periodCovered) {
                                        val period = plans.firstOrNull { it.id == item.planId }?.billingPeriod
                                        queue[index] = item.copy(
                                            ok = false,
                                            result = if (period != null) {
                                                duplicateMembershipPeriodMessage(item.planLabel, period)
                                            } else {
                                                "Periodo ya cubierto · no se crea orden duplicada"
                                            },
                                            orderId = null,
                                        )
                                        failCount++
                                    } else if (item.isCourtesy) {
                                        val order = AdminRepo.api.createMembershipOrder(
                                            AdminMembershipOrderUpsert(
                                                clientId = item.client.id,
                                                planId = item.planId,
                                                branchId = companyId,
                                                trainerId = item.trainerId,
                                                method = null,
                                                status = AdminMembershipOrderStatus.COURTESY,
                                                orderDate = item.startsAt,
                                                amountCents = item.amountCents,
                                                currency = GymlyCurrency.CODE,
                                                notes = "Alta masiva · cortesía · ${item.planLabel}",
                                            ),
                                        )
                                        queue[index] = item.copy(
                                            ok = true,
                                            result = "orden ${order.displayNumber()} · cortesía",
                                            orderId = order.id,
                                        )
                                        okCount++
                                    } else {
                                            val order = AdminRepo.api.createMembershipOrder(
                                                AdminMembershipOrderUpsert(
                                                    clientId = item.client.id,
                                                    planId = item.planId,
                                                    branchId = companyId,
                                                    trainerId = item.trainerId,
                                                    method = primaryMethod,
                                                    status = if (bulkPaymentsAlwaysPaid || item.discount != null) AdminMembershipOrderStatus.PAID else AdminMembershipOrderStatus.PENDING,
                                                    orderDate = item.startsAt,
                                                    amountCents = item.amountCents,
                                                    pendingUsdCents = if (skipPayment) {
                                                        0L
                                                    } else {
                                                        item.pendingUsdCents.takeIf { it > 0L }
                                                            ?: parseBulkAmountToCents(sharedPendingUsdInput)
                                                    },
                                                    pendingBsCents = if (skipPayment) 0L else item.pendingBsCents,
                                                    currency = GymlyCurrency.CODE,
                                                    notes = "Alta masiva · ${item.planLabel}",
                                                ),
                                            )
                                            var walletNote = ""
                                            if (!skipPayment && walletModuleEnabled && item.walletApplyCents > 0) {
                                                AdminRepo.api.applyClientAccountCredit(
                                                    item.client.id,
                                                    AdminClientAccountApplyCredit(
                                                        membershipOrderId = order.id,
                                                        amountCents = item.walletApplyCents,
                                                    ),
                                                )
                                                walletApplyNotes++
                                                walletNote = " · monedero ${formatMoney(item.walletApplyCents.toInt(), GymlyCurrency.CODE)}"
                                            }
                                            val remaining = item.remainingCents
                                            queue[index] = item.copy(
                                                ok = true,
                                                result = "orden ${order.displayNumber()} · ${order.statusLabel.lowercase()}" +
                                                    walletNote +
                                                    if (skipPayment) " · cobro pendiente" else "",
                                                orderId = order.id,
                                            )
                                            if (!skipPayment && remaining > 0) {
                                                collectTargets += AdminPaymentCollectTarget(
                                                    clientId = item.client.id,
                                                    membershipOrderId = order.id,
                                                    amountCents = remaining,
                                                    planId = item.planId,
                                                    branchId = companyId,
                                                    trainerId = item.trainerId,
                                                    discount = item.discount,
                                                )
                                            }
                                            okCount++
                                    }
                                } catch (e: Throwable) {
                                    val pending = pendingApprovals.offer(e)
                                    queue[index] = item.copy(
                                        ok = pending,
                                        result = if (pending) "Enviada a aprobación" else e.userMessage("Error"),
                                    )
                                    if (pending) okCount++ else failCount++
                                }
                                onProgress(index + 1, queue.size)
                            }

                            if (!skipPayment && failCount == 0 && chargeable.isNotEmpty() &&
                                (collectTargets.isNotEmpty() || walletDeposits.isNotEmpty())
                            ) {
                                val collectTotal = collectTargets.sumOf { it.amountCents }
                                val walletDepositTotal = walletDeposits.sumOf { it.shareCents }
                                val dueForPayment = collectTotal + walletDepositTotal
                                val (_, collectLinesPrepared) = prepareBulkCollectLines(
                                    entries = listOf(chargeableTotalInput to lines),
                                    fallbackAmountInput = chargeableTotalInput,
                                    quote = exchangeQuote,
                                )
                                validateBulkCollectCoverage(
                                    dueBsTotal = collectTotal,
                                    collectLines = collectLinesPrepared,
                                )?.let { msg ->
                                    throw IllegalStateException(msg)
                                }
                                val paymentTotal = collectLinesPrepared.sumOf { it.amountCents }.coerceAtLeast(0L)
                                val excess = (paymentTotal - dueForPayment).coerceAtLeast(0)
                                val changeOnce = bulkSingleChangeGivenCents(listOf(sharedChangeGivenCents))

                                when {
                                    collectTargets.isNotEmpty() && walletDeposits.isEmpty() -> {
                                        val payments = AdminRepo.api.collectPayments(
                                            AdminPaymentCollect(
                                                currency = GymlyCurrency.CODE,
                                                targets = collectTargets,
                                                lines = collectLinesPrepared,
                                                paidAt = paymentDateToPaidAtIso(
                                                    chargeable.firstOrNull()?.paidAt ?: todayIsoDate(),
                                                ),
                                                changeGivenCents = changeOnce,
                                            ),
                                        )
                                        lines.forEachIndexed { lineIndex, line ->
                                            line.proof?.let { proof ->
                                                payments.getOrNull(lineIndex)?.let { payment ->
                                                    AdminRepo.api.uploadPaymentProof(payment.id, proof)
                                                }
                                            }
                                        }
                                    }
                                    walletDeposits.isNotEmpty() && collectTargets.isEmpty() -> {
                                        AdminRepo.api.depositToClientWallets(
                                            AdminClientWalletDepositBatch(
                                                deposits = walletDeposits,
                                                paymentTotalCents = paymentTotal.coerceAtLeast(walletDepositTotal),
                                                description = "Alta masiva · abono monedero digital",
                                            ),
                                        )
                                    }
                                    collectTargets.isNotEmpty() && walletDeposits.isNotEmpty() -> {
                                        AdminRepo.api.depositToClientWallets(
                                            AdminClientWalletDepositBatch(
                                                deposits = walletDeposits,
                                                paymentTotalCents = walletDepositTotal + excess / 2,
                                                description = "Alta masiva · periodo cubierto / monedero",
                                            ),
                                        )
                                        val collectLines = listOf(
                                            AdminPaymentCollectLine(
                                                method = lines.first().method,
                                                amountCents = collectTotal + excess - excess / 2,
                                                foreignUsdCents = null,
                                                payerPhone = null,
                                                notes = normalizePaymentReference(lines.first().method, lines.first().notes)
                                                    .ifBlank { null },
                                            ),
                                        )
                                        val payments = AdminRepo.api.collectPayments(
                                            AdminPaymentCollect(
                                                currency = GymlyCurrency.CODE,
                                                targets = collectTargets,
                                                lines = collectLines,
                                                paidAt = paymentDateToPaidAtIso(
                                                    chargeable.firstOrNull()?.paidAt ?: todayIsoDate(),
                                                ),
                                                changeGivenCents = changeOnce,
                                            ),
                                        )
                                        lines.firstOrNull()?.proof?.let { proof ->
                                            payments.firstOrNull()?.let { AdminRepo.api.uploadPaymentProof(it.id, proof) }
                                        }
                                    }
                                }
                            }

                            val excessHint = if (!skipPayment && remainingDue > 0) {
                                val paymentTotal = paymentLinesBsCents(
                                    lines,
                                    chargeableTotalInput,
                                    exchangeQuote,
                                ).coerceAtLeast(0L)
                                val overpay = (paymentTotal - remainingDue).coerceAtLeast(0L)
                                val change = sharedChangeGivenCents.coerceIn(0L, paymentTotal)
                                val walletCredit = if (walletModuleEnabled) {
                                    (overpay - change).coerceAtLeast(0L)
                                } else {
                                    0L
                                }
                                overpayDispositionNote(change, walletCredit)
                            } else {
                                ""
                            }

                            val dupCount = queue.count {
                                it.ok == false && it.result.orEmpty().contains("duplicado", ignoreCase = true)
                            }
                            val dupHint = if (dupCount > 0) {
                                " · $dupCount plan/periodo duplicado(s)"
                            } else {
                                ""
                            }
                            onDoneMessage(
                                "Órdenes: $okCount ok · $failCount con error$dupHint" +
                                    (if (walletApplyNotes > 0) " · $walletApplyNotes con monedero" else "") +
                                    (if (walletNotes > 0) " · $walletNotes abonos monedero" else "") +
                                    excessHint,
                            )
                            processSummary = "$okCount correctos · $failCount con error$dupHint" +
                                (if (walletApplyNotes > 0) " · $walletApplyNotes monedero aplicado" else "") +
                                (if (walletNotes > 0) " · $walletNotes monedero" else "")
                            if (dupCount > 0) {
                                onError(
                                    "Hay $dupCount socio(s) con plan/periodo duplicado. " +
                                        "Revisa el detalle: no se puede cobrar el mismo plan dos veces en el mismo periodo.",
                                )
                            }
                            processResults = queue.map { item ->
                                BulkProcessResultItem(
                                    title = item.client.fullName,
                                    subtitle = item.result,
                                    ok = item.ok == true,
                                )
                            }
                        } catch (e: Throwable) {
                            // Cobro con descuento: queda en Aprobaciones. No es un error; cuenta como correcto.
                            val pending = pendingApprovals.offer(e)
                            val msg = if (pending) "cobro enviado a aprobación" else e.userMessage("Error al registrar cobro")
                            if (!pending) onError(msg)
                            // Órdenes ya creadas: si el cobro no se aplicó, se marca (no fingir éxito).
                            queue.forEachIndexed { index, item ->
                                if (item.orderId != null && item.ok == true && !item.isCourtesy) {
                                    queue[index] = item.copy(
                                        ok = pending,
                                        result = listOfNotNull(
                                            item.result,
                                            if (pending) msg else "cobro no aplicado: $msg",
                                        ).joinToString(" · "),
                                    )
                                }
                            }
                            processResults = queue.map { item ->
                                BulkProcessResultItem(
                                    title = item.client.fullName,
                                    subtitle = item.result,
                                    ok = item.ok == true,
                                )
                            }
                        } finally {
                            onProcessingChange(false)
                        }
                    }
                },
            )
    }

    // Cobro del socio que aún está en la tarjeta (antes de agregarlo a la lista).
    if (draftPaymentDialogOpen) {
        val client = selected
        val plan = plans.firstOrNull { it.id == draftPlanId }
        if (client == null || plan == null) {
            draftPaymentDialogOpen = false
        } else {
            BulkRowPaymentDialog(
                personLabel = client.fullName,
                defaultAmount = centsToAmountInput(draftAmountCents),
                defaultUsdAmount = plan.priceUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                initialStatus = AdminMembershipOrderStatus.PAID,
                initialMethod = AdminPaymentMethod.CASH,
                initialAmount = centsToAmountInput(draftAmountCents),
                initialPayerPhone = "",
                clientId = client.id,
                initialLines = draftPaymentLines,
                initialChangeGivenCents = draftChangeGivenCents,
                initialPendingUsd = draftPendingUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                initialPendingBs = draftPendingBsCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                checkExistingMobilePayments = true,
                paymentDateIso = draftPaidAt.ifBlank { todayIsoDate() },
                initialDiscount = draftDiscount,
                allowDiscount = true,
                onDismiss = { draftPaymentDialogOpen = false },
                onConfirmResult = { result ->
                    if (result.status == AdminMembershipOrderStatus.COURTESY) {
                        draftWalletApplyCents = 0L
                        draftPaymentLines = emptyList()
                        draftDiscount = null
                        draftChangeGivenCents = 0L
                        draftPendingUsdCents = 0L
                        draftPendingBsCents = 0L
                        draftPaymentConfigured = false
                    } else {
                        draftWalletApplyCents = result.walletAppliedCents.coerceAtMost(draftAmountCents)
                        draftPaymentLines = result.lines
                        draftDiscount = result.discount
                        draftChangeGivenCents = result.changeGivenCents
                        draftPendingUsdCents = result.pendingUsdCents
                        draftPendingBsCents = result.pendingBsCents
                        draftPaymentConfigured = true
                    }
                    draftPaymentDialogOpen = false
                },
            )
        }
    }

    // Cobro de un socio que ya está en la lista.
    paymentRowIndex?.let { rowIndex ->
        if (rowIndex !in queue.indices) {
            paymentRowIndex = null
        } else {
            val item = queue[rowIndex]
            val planUsd = plans.firstOrNull { it.id == item.planId }?.priceUsdCents ?: 0L
            val planUsdInput = planUsd.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty()
            val otherRowRefs = queue.filterIndexed { i, _ -> i != rowIndex }
                .flatMap { other ->
                    other.paymentLines.mapNotNull { line ->
                        if (line.method != AdminPaymentMethod.MOBILE_PAYMENT) null
                        else paymentReferenceLookupKey(line.method, line.notes).takeIf { it.length >= 5 }
                    }
                }.toSet()
            BulkRowPaymentDialog(
                personLabel = item.client.fullName,
                defaultAmount = centsToAmountInput(item.amountCents),
                defaultUsdAmount = planUsdInput,
                initialStatus = AdminMembershipOrderStatus.PAID,
                initialMethod = AdminPaymentMethod.CASH,
                initialAmount = centsToAmountInput(item.amountCents),
                initialPayerPhone = "",
                clientId = item.client.id,
                initialLines = item.paymentLines,
                initialChangeGivenCents = item.changeGivenCents,
                initialPendingUsd = item.pendingUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                extraKnownRefs = otherRowRefs,
                checkExistingMobilePayments = true,
                paymentDateIso = item.paidAt.ifBlank { todayIsoDate() },
                initialDiscount = item.discount,
                allowDiscount = true,
                onDismiss = { paymentRowIndex = null },
                onConfirmResult = { result ->
                    queue[rowIndex] = if (result.status == AdminMembershipOrderStatus.COURTESY) {
                        item.copy(
                            isCourtesy = true,
                            amountCents = 0L,
                            walletApplyCents = 0L,
                            paymentLines = emptyList(),
                            changeGivenCents = 0L,
                            pendingUsdCents = 0L,
                            pendingBsCents = 0L,
                            discount = null,
                            paymentConfigured = false,
                        )
                    } else {
                        item.copy(
                            walletApplyCents = result.walletAppliedCents.coerceAtMost(item.amountCents),
                            paymentLines = result.lines,
                            discount = result.discount,
                            changeGivenCents = result.changeGivenCents,
                            changeGivenUsdCents = result.changeGivenUsdCents,
                            pendingUsdCents = result.pendingUsdCents,
                            pendingBsCents = result.pendingBsCents,
                            paymentConfigured = true,
                        )
                    }
                    sharedPaymentError = null
                    paymentRowIndex = null
                },
            )
        }
    }

    processResults?.let { items ->
        BulkProcessResultDialog(
            title = if (registerWithoutPayment) "Órdenes registradas" else "Cobro procesado",
            summary = processSummary,
            items = items,
            onDismiss = {
                processResults = null
                queue.clear()
                sharedPaymentLines = emptyList()
                paymentConfigured = false
                registerWithoutPayment = false
                sharedPaymentError = null
            },
            onPrimary = {
                processResults = null
                queue.clear()
                sharedPaymentLines = emptyList()
                paymentConfigured = false
                registerWithoutPayment = false
                sharedPaymentError = null
            },
        )
    }
}

@Composable
private fun BulkPaymentSearchHit(client: AdminClient, onSelect: () -> Unit) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .clickable(onClick = onSelect)
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        BulkClientPhotoPreview(client = client, size = 52.dp)
        Column(Modifier.weight(1f)) {
            Text(client.fullName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
            Text(
                listOfNotNull(client.email, client.docId?.let { "Cédula $it" }).joinToString(" · "),
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
    }
}

@Composable
private fun BulkPaymentAddMemberCard(
    client: AdminClient,
    plans: List<AdminMembershipPlanItem>,
    trainers: List<AdminTrainer>,
    planId: String?,
    trainerId: String?,
    paidAt: String,
    startsAt: String,
    endsAt: String,
    amountCents: Long,
    isCourtesy: Boolean,
    editing: Boolean = false,
    processing: Boolean,
    /** Modo por socio: se configura el cobro aquí mismo antes de agregar a la lista. */
    showPaymentStep: Boolean = true,
    paymentConfigured: Boolean = false,
    paymentSummary: String? = null,
    /** Desglose del cobro en borrador (métodos, vuelto, descuento, pendientes en Bs y $). */
    paymentBreakdown: List<BreakdownRow> = emptyList(),
    walletApplyCents: Long = 0L,
    draftError: String? = null,
    onPlan: (String?) -> Unit,
    onTrainer: (String?) -> Unit,
    onPaidAt: (String) -> Unit,
    onStartsAt: (String) -> Unit,
    onEndsAt: (String) -> Unit,
    onConfigurePayment: () -> Unit = {},
    onClearPayment: () -> Unit = {},
    onClear: () -> Unit,
    onAdd: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                if (editing) "Editar socio de la lista" else "Socio",
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = AdminColors.Text,
            )
            OutlinedButton(
                onClick = onClear,
                enabled = !processing,
                shape = RoundedCornerShape(10.dp),
            ) {
                Icon(Icons.Outlined.Search, contentDescription = null, tint = AdminColors.Primary, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(6.dp))
                Text(
                    if (editing) "Cancelar edición" else "Buscar otro",
                    color = AdminColors.Primary,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
        Spacer(Modifier.height(12.dp))
        val selectedPlan = plans.firstOrNull { it.id == planId }
        val startsIso = startsAt.ifBlank { bulkFormTodayIsoDate() }
        val endsIso = endsAt.ifBlank {
            selectedPlan?.let { planEndsAtFromStart(startsIso, it.billingPeriod) }.orEmpty()
        }
        val usdDisplay = selectedPlan?.priceUsdCents?.takeIf { it > 0 }
            ?.let { formatMoney(it.toInt(), "USD") }
            .orEmpty()
        // Celular (ancho < 600 dp): la tarjeta se apila para que el nombre y los campos tengan espacio.
        BoxWithConstraints(Modifier.fillMaxWidth()) {
        val narrowCard = maxWidth < 600.dp
        if (narrowCard) {
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                BulkClientPhotoPreview(client = client, size = 72.dp)
                Text(client.fullName, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = AdminColors.Text)
                Text(
                    listOfNotNull(client.email, client.phone, client.docId?.let { "Céd. $it" }).joinToString(" · "),
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    MembershipFieldDropdown(
                        label = "Plan",
                        value = selectedPlan?.name ?: "Seleccionar",
                        options = plans.map { it.id to it.name },
                        onSelect = onPlan,
                        modifier = Modifier.weight(1f),
                    )
                    MembershipFieldDropdown(
                        label = "Entrenador",
                        value = trainers.firstOrNull { it.id == trainerId }?.displayName ?: "Sin entrenador",
                        options = listOf(null to "Sin entrenador") + trainers.map { it.id to it.displayName },
                        onSelect = onTrainer,
                        modifier = Modifier.weight(1f),
                    )
                }
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Column(Modifier.weight(1f)) {
                        AdminDateField(
                            label = "Pago",
                            value = paidAt.ifBlank { bulkFormTodayIsoDate() },
                            onValueChange = onPaidAt,
                            enabled = !processing,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                    }
                    Column(Modifier.weight(1f)) {
                        AdminDateField(
                            label = "Inicio",
                            value = startsIso,
                            onValueChange = onStartsAt,
                            enabled = !processing,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                    }
                }
                AdminDateField(
                    label = "Fecha de Vencimiento",
                    value = endsIso,
                    onValueChange = onEndsAt,
                    enabled = !processing,
                    placeholder = "AAAA-MM-DD",
                    outputFormat = AdminDateOutputFormat.ISO,
                )
                Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.End) {
                    Text("Cuota del plan", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Text(
                        if (isCourtesy) {
                            "Cortesía"
                        } else {
                            buildString {
                                append(formatMoney(amountCents.toInt(), GymlyCurrency.CODE))
                                if (usdDisplay.isNotBlank()) {
                                    append(" · ")
                                    append(usdDisplay)
                                }
                            }
                        },
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = if (isCourtesy) AdminColors.Primary else AdminColors.Text,
                        textAlign = androidx.compose.ui.text.style.TextAlign.End,
                    )
                }
            }
        } else {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.Top,
        ) {
            BulkClientPhotoPreview(client = client, size = 96.dp)
            Column(Modifier.weight(1f)) {
                Text(client.fullName, fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    listOfNotNull(client.email, client.phone, client.docId?.let { "Céd. $it" })
                        .joinToString(" · "),
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
                Spacer(Modifier.height(12.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    MembershipFieldDropdown(
                        label = "Plan",
                        value = selectedPlan?.name ?: "Seleccionar",
                        options = plans.map { it.id to it.name },
                        onSelect = onPlan,
                        modifier = Modifier.weight(1f),
                    )
                    MembershipFieldDropdown(
                        label = "Entrenador",
                        value = trainers.firstOrNull { it.id == trainerId }?.displayName ?: "Sin entrenador",
                        options = listOf(null to "Sin entrenador") + trainers.map { it.id to it.displayName },
                        onSelect = onTrainer,
                        modifier = Modifier.weight(1f),
                    )
                    Column(Modifier.weight(0.85f)) {
                        AdminDateField(
                            label = "Pago",
                            value = paidAt.ifBlank { bulkFormTodayIsoDate() },
                            onValueChange = onPaidAt,
                            enabled = !processing,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                        Text(
                            "Por defecto hoy; editable.",
                            color = AdminColors.TextMuted,
                            fontSize = 10.sp,
                            modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                        )
                    }
                    Column(Modifier.weight(0.85f)) {
                        AdminDateField(
                            label = "Inicio",
                            value = startsIso,
                            onValueChange = onStartsAt,
                            enabled = !processing,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                        Text(
                            "Inicio de vigencia.",
                            color = AdminColors.TextMuted,
                            fontSize = 10.sp,
                            modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                        )
                    }
                    Column(Modifier.weight(0.95f)) {
                        AdminDateField(
                            label = "Fecha de Vencimiento",
                            value = endsIso,
                            onValueChange = onEndsAt,
                            enabled = !processing,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                        Text(
                            "Se recalcula con inicio/plan; editable.",
                            color = AdminColors.TextMuted,
                            fontSize = 10.sp,
                            modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                        )
                    }
                }
            }
            Column(
                Modifier.width(180.dp),
                horizontalAlignment = Alignment.End,
            ) {
                Text("Cuota del plan", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                Text(
                    if (isCourtesy) {
                        "Cortesía"
                    } else {
                        buildString {
                            append(formatMoney(amountCents.toInt(), GymlyCurrency.CODE))
                            if (usdDisplay.isNotBlank()) {
                                append(" · ")
                                append(usdDisplay)
                            }
                        }
                    },
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = if (isCourtesy) AdminColors.Primary else AdminColors.Text,
                    textAlign = androidx.compose.ui.text.style.TextAlign.End,
                )
                if (!isCourtesy && usdDisplay.isNotBlank()) {
                    Text(
                        "Bs. del plan · $ catálogo",
                        color = AdminColors.TextMuted,
                        fontSize = 10.sp,
                        modifier = Modifier.padding(top = 2.dp),
                        textAlign = androidx.compose.ui.text.style.TextAlign.End,
                    )
                }
            }
        }
        }
        }
        if (!isCourtesy && amountCents > 0) {
            if (!paymentConfigured) {
                Spacer(Modifier.height(12.dp))
                ClientWalletBalanceHint(clientId = client.id)
            }
            if (showPaymentStep) {
                Spacer(Modifier.height(if (paymentConfigured) 12.dp else 10.dp))
                BulkPaymentDraftPaymentCell(
                    amountCents = amountCents,
                    walletApplyCents = walletApplyCents,
                    configured = paymentConfigured,
                    summary = paymentSummary,
                    breakdown = paymentBreakdown,
                    enabled = !processing,
                    onConfigure = onConfigurePayment,
                    onClearPayment = onClearPayment,
                )
            }
        }
        draftError?.let {
            Spacer(Modifier.height(10.dp))
            Text(it, color = AdminColors.Error, fontSize = 13.sp, fontWeight = FontWeight.Medium)
        }
        Spacer(Modifier.height(12.dp))
        Button(
            onClick = onAdd,
            enabled = !processing && draftError.isNullOrBlank(),
            modifier = Modifier.fillMaxWidth(),
            colors = ButtonDefaults.buttonColors(
                containerColor = AdminColors.Primary,
                contentColor = AdminColors.OnBrand,
            ),
            shape = RoundedCornerShape(10.dp),
        ) {
            Text(
                if (editing) "Guardar cambios en la lista" else "Agregar a la lista",
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (showPaymentStep && !isCourtesy && amountCents > 0 && !paymentConfigured) {
            Spacer(Modifier.height(6.dp))
            Text(
                "Puedes agregarlo sin cobro y configurarlo después desde la lista.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
    }
}

/** Estado del cobro de un socio: sin configurar, o con el desglose ya cargado. */
@Composable
private fun BulkPaymentDraftPaymentCell(
    amountCents: Long,
    walletApplyCents: Long,
    configured: Boolean,
    summary: String?,
    /** Desglose detallado del cobro (métodos, vuelto, descuento, pendientes). Vacío = solo el resumen. */
    breakdown: List<BreakdownRow> = emptyList(),
    enabled: Boolean,
    onConfigure: () -> Unit,
    onClearPayment: () -> Unit,
) {
    val remaining = (amountCents - walletApplyCents).coerceAtLeast(0L)
    val cardMod = Modifier
        .fillMaxWidth()
        .clip(RoundedCornerShape(12.dp))
        .background(
            if (configured) AdminColors.Primary.copy(alpha = 0.10f) else AdminColors.Surface,
        )
        .border(
            1.dp,
            if (configured) AdminColors.Primary.copy(alpha = 0.45f) else AdminColors.Outline.copy(alpha = 0.6f),
            RoundedCornerShape(12.dp),
        )
        .padding(12.dp)
    // Celular (ancho < 600 dp): el texto va arriba y el botón Cobrar ocupa todo el ancho abajo.
    BoxWithConstraints(Modifier.fillMaxWidth()) {
    if (maxWidth < 600.dp) {
        Column(cardMod, verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    Icons.Outlined.Payments,
                    contentDescription = null,
                    tint = if (configured) AdminColors.Primary else AdminColors.TextMuted,
                    modifier = Modifier.size(20.dp),
                )
                Spacer(Modifier.width(10.dp))
                Column(Modifier.weight(1f)) {
                    Text(
                        if (configured) "Cobro configurado" else "Cobro sin configurar",
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                        color = AdminColors.Text,
                    )
                    BulkPaymentSummaryText(configured = configured, summary = summary, breakdown = breakdown)
                    if (walletApplyCents > 0) {
                        Text(
                            "Monedero ${formatMoney(walletApplyCents.toInt(), GymlyCurrency.CODE)} · " +
                                "por cobrar ${formatMoney(remaining.toInt(), GymlyCurrency.CODE)}",
                            color = AdminColors.Primary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                }
                if (configured) {
                    Text(
                        "×",
                        color = AdminColors.Error,
                        fontSize = 20.sp,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier
                            .clickable(enabled = enabled, onClick = onClearPayment)
                            .padding(8.dp),
                    )
                }
            }
            Button(
                onClick = onConfigure,
                enabled = enabled,
                modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                    disabledContainerColor = AdminColors.Outline.copy(alpha = 0.45f),
                    disabledContentColor = AdminColors.TextMuted,
                ),
                elevation = ButtonDefaults.buttonElevation(
                    defaultElevation = 3.dp,
                    pressedElevation = 1.dp,
                ),
            ) {
                Icon(
                    Icons.Outlined.Payments,
                    contentDescription = null,
                    modifier = Modifier.size(22.dp),
                    tint = AdminColors.OnBrand,
                )
                Spacer(Modifier.width(8.dp))
                Text("Cobrar", fontWeight = FontWeight.Bold, fontSize = 16.sp)
            }
        }
    } else {
    Row(
        cardMod,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            Icons.Outlined.Payments,
            contentDescription = null,
            tint = if (configured) AdminColors.Primary else AdminColors.TextMuted,
            modifier = Modifier.size(20.dp),
        )
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1f)) {
            Text(
                if (configured) "Cobro configurado" else "Cobro sin configurar",
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
                color = AdminColors.Text,
            )
            BulkPaymentSummaryText(configured = configured, summary = summary, breakdown = breakdown)
            if (walletApplyCents > 0) {
                Text(
                    "Monedero ${formatMoney(walletApplyCents.toInt(), GymlyCurrency.CODE)} · " +
                        "por cobrar ${formatMoney(remaining.toInt(), GymlyCurrency.CODE)}",
                    color = AdminColors.Primary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
        if (configured) {
            Text(
                "×",
                color = AdminColors.Error,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier
                    .clickable(enabled = enabled, onClick = onClearPayment)
                    .padding(8.dp),
            )
        }
        Spacer(Modifier.width(4.dp))
        Button(
            onClick = onConfigure,
            enabled = enabled,
            modifier = Modifier
                .heightIn(min = 52.dp)
                .widthIn(min = 148.dp),
            shape = RoundedCornerShape(14.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = AdminColors.Primary,
                contentColor = AdminColors.OnBrand,
                disabledContainerColor = AdminColors.Outline.copy(alpha = 0.45f),
                disabledContentColor = AdminColors.TextMuted,
            ),
            elevation = ButtonDefaults.buttonElevation(
                defaultElevation = 3.dp,
                pressedElevation = 1.dp,
            ),
        ) {
            Icon(
                Icons.Outlined.Payments,
                contentDescription = null,
                modifier = Modifier.size(22.dp),
                tint = AdminColors.OnBrand,
            )
            Spacer(Modifier.width(8.dp))
            Text("Cobrar", fontWeight = FontWeight.Bold, fontSize = 16.sp)
        }
    }
    }
    }
}

@Composable
private fun BulkPaymentQueueRow(
    item: BulkPaymentQueueItem,
    plans: List<AdminMembershipPlanItem>,
    processing: Boolean,
    /** Modo por socio: celda de cobro propia. Modo pago único: banner de monedero. */
    perClientPayment: Boolean,
    paymentSummary: String? = null,
    onRemove: () -> Unit,
    onEdit: () -> Unit = {},
    onConfigurePayment: () -> Unit = {},
    onClearPayment: () -> Unit = {},
    onWalletApply: (Long) -> Unit = {},
) {
    var walletModuleEnabled by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        walletModuleEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(false)
    }
    val plan = plans.firstOrNull { it.id == item.planId }
    val endsIso = item.endsAt.ifBlank {
        plan?.let { planEndsAtFromStart(item.startsAt, it.billingPeriod) }.orEmpty()
    }
    val usdLabel = plan?.priceUsdCents?.takeIf { it > 0 }
        ?.let { formatMoney(it.toInt(), "USD") }
        .orEmpty()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            BulkClientPhotoPreview(client = item.client, size = 48.dp)
            Column(Modifier.weight(1f)) {
                Text(item.client.fullName, fontWeight = FontWeight.SemiBold, fontSize = 14.sp, color = AdminColors.Text)
                Text(
                    buildString {
                        append(item.planLabel)
                        append(" · ")
                        append(item.trainerLabel)
                        append(" · inicio ")
                        append(item.startsAt)
                        if (endsIso.isNotBlank()) {
                            append(" · vence ")
                            append(endsIso)
                        }
                        if (item.isCourtesy) append(" · Cortesía")
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                if (walletModuleEnabled && item.walletApplyCents > 0) {
                    Text(
                        "Monedero: −${formatMoney(item.walletApplyCents.toInt(), GymlyCurrency.CODE)}" +
                            " · resta ${formatMoney(item.remainingCents.toInt(), GymlyCurrency.CODE)}",
                        color = AdminColors.Primary,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
                item.discount?.let { discount ->
                    Text(
                        "Descuento ${formatMoney(discount.cents.toInt(), GymlyCurrency.CODE)}" +
                            (BULK_DISCOUNT_REASONS.firstOrNull { it.first == discount.reason }?.second?.let { " · $it" } ?: "") +
                            " · cobra ${formatMoney(item.remainingCents.toInt(), GymlyCurrency.CODE)}",
                        color = AdminColors.Primary,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
            Text(
                if (item.isCourtesy) {
                    "—"
                } else {
                    buildString {
                        append(formatMoney(item.amountCents.toInt(), GymlyCurrency.CODE))
                        if (usdLabel.isNotBlank()) {
                            append(" · ")
                            append(usdLabel)
                        }
                    }
                },
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = AdminColors.Text,
            )
            Text(
                "Editar",
                color = AdminColors.Primary,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.clickable(enabled = !processing && item.ok != true, onClick = onEdit),
            )
            Text(
                "Quitar",
                color = AdminColors.Error,
                fontSize = 12.sp,
                modifier = Modifier.clickable(enabled = !processing, onClick = onRemove),
            )
        }
        if (!item.isCourtesy && item.amountCents > 0 && item.ok != true) {
            Spacer(Modifier.height(10.dp))
            if (perClientPayment) {
                BulkPaymentDraftPaymentCell(
                    amountCents = item.amountCents,
                    walletApplyCents = item.walletApplyCents,
                    configured = item.paymentConfigured,
                    summary = paymentSummary,
                    breakdown = buildBulkPaymentBreakdown(
                        lines = item.paymentLines,
                        walletApplyCents = item.walletApplyCents,
                        discountBsCents = item.discount?.cents ?: 0L,
                        discountUsdCents = item.discount?.usdCents ?: 0L,
                        changeBsCents = item.changeGivenCents,
                        changeUsdCents = item.changeGivenUsdCents,
                        pendingBsCents = item.pendingBsCents,
                        pendingUsdCents = item.pendingUsdCents,
                        dueBsCents = item.remainingCents,
                    ),
                    enabled = !processing,
                    onConfigure = onConfigurePayment,
                    onClearPayment = onClearPayment,
                )
            } else if (walletModuleEnabled) {
                ClientAccountCreditBanner(
                    clientId = item.client.id,
                    pendingAmountCents = if (item.remainingCents > 0) item.remainingCents else item.amountCents,
                    enabled = !processing,
                    deferApply = true,
                    highlighted = true,
                    initialAppliedCents = item.walletApplyCents,
                    onCreditApplied = { applied, _ -> onWalletApply(applied) },
                )
            }
        }
    }
}

@Composable
private fun BulkPaymentEmptyQueueHint() {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer.copy(alpha = 0.5f))
            .padding(20.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Lista vacía", fontWeight = FontWeight.SemiBold, fontSize = 15.sp, color = AdminColors.Text)
        Spacer(Modifier.height(6.dp))
        Text(
            "Busca un socio arriba y pulsa «Agregar a la lista». Puedes agregar varios antes de cobrar.",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
    }
}

/** Resumen del modo por socio: total, monedero y cuántos socios faltan por configurar. */
@Composable
private fun BulkPaymentPerClientSummary(
    grossChargeableTotal: Long,
    walletAppliedTotal: Long,
    chargeableTotal: Long,
    chargeableDualLabel: String,
    chargeableCount: Int,
    pendingCount: Int,
    paidLabel: String?,
    registerWithoutPayment: Boolean,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(16.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Bottom,
        ) {
            Column {
                Text("Total a cobrar", color = AdminColors.TextMuted, fontSize = 12.sp)
                Text(
                    chargeableDualLabel,
                    fontWeight = FontWeight.Bold,
                    fontSize = 28.sp,
                    color = AdminColors.Primary,
                )
                if (walletAppliedTotal > 0) {
                    Text(
                        "Plan ${formatMoney(grossChargeableTotal.toInt(), GymlyCurrency.CODE)}" +
                            " − monedero ${formatMoney(walletAppliedTotal.toInt(), GymlyCurrency.CODE)}",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
            Text(
                "$chargeableCount ${if (chargeableCount == 1) "socio" else "socios"}",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
        }
        if (!paidLabel.isNullOrBlank() && paidLabel != "—") {
            Spacer(Modifier.height(6.dp))
            Text(
                "Cargado: $paidLabel",
                color = AdminColors.Primary,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
        }
        Spacer(Modifier.height(12.dp))
        Text(
            when {
                registerWithoutPayment ->
                    "Las órdenes quedarán pendientes de cobro; el cobro de cada socio se ignora."
                pendingCount > 0 ->
                    "Falta configurar el cobro de $pendingCount " +
                        (if (pendingCount == 1) "socio" else "socios") +
                        ". Usa «Cobrar» en su fila de la lista."
                chargeableCount == 0 -> "Todos en la lista son cortesía. No hace falta método de pago."
                else -> "Cada socio tiene su cobro listo. Al confirmar se registra un pago por socio."
            },
            color = if (pendingCount > 0 && !registerWithoutPayment) AdminColors.Error else AdminColors.TextMuted,
            fontSize = 12.sp,
            fontWeight = if (pendingCount > 0 && !registerWithoutPayment) FontWeight.Medium else FontWeight.Normal,
        )
    }
}

// DEUDA TÉCNICA: sin llamadas desde la pantalla; el modo pago único quedó sin acceso. Ver docs/deuda-tecnica.md
@Composable
private fun BulkPaymentModeSelector(
    mode: BulkPaymentMode,
    enabled: Boolean,
    walletModuleEnabled: Boolean = false,
    onModeChange: (BulkPaymentMode) -> Unit,
) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        BulkPaymentModeChip(
            title = "Cada socio con su pago",
            hint = if (walletModuleEnabled) {
                "Cobras a cada uno por separado, con su monedero."
            } else {
                "Cobras a cada uno por separado."
            },
            selected = mode == BulkPaymentMode.PerClient,
            enabled = enabled,
            modifier = Modifier.weight(1f),
            onClick = { onModeChange(BulkPaymentMode.PerClient) },
        )
        BulkPaymentModeChip(
            title = "Un solo pago para varios",
            hint = "Una persona paga por toda la lista con un mismo cobro.",
            selected = mode == BulkPaymentMode.Shared,
            enabled = enabled,
            modifier = Modifier.weight(1f),
            onClick = { onModeChange(BulkPaymentMode.Shared) },
        )
    }
}

@Composable
private fun BulkPaymentModeChip(
    title: String,
    hint: String,
    selected: Boolean,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Column(
        modifier
            .clip(RoundedCornerShape(12.dp))
            .background(
                if (selected) AdminColors.Primary.copy(alpha = 0.16f) else AdminColors.Surface,
            )
            .border(
                if (selected) 2.dp else 1.dp,
                if (selected) AdminColors.Primary.copy(alpha = 0.7f) else AdminColors.Outline.copy(alpha = 0.6f),
                RoundedCornerShape(12.dp),
            )
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Text(
            title,
            color = if (selected) AdminColors.Primary else AdminColors.Text,
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
        )
        Text(hint, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 2)
    }
}

@Composable
private fun BulkPaymentCheckoutSection(
    mode: BulkPaymentMode,
    queueSize: Int,
    chargeableCount: Int,
    grossChargeableTotal: Long,
    chargeableTotal: Long,
    chargeableDualLabel: String,
    walletAppliedTotal: Long,
    walletModuleEnabled: Boolean,
    chargeableTotalInput: String,
    paymentConfigured: Boolean,
    registerWithoutPayment: Boolean,
    paymentSummary: String?,
    paymentError: String?,
    processing: Boolean,
    onModeChange: (BulkPaymentMode) -> Unit,
    onRegisterWithoutPaymentChange: (Boolean) -> Unit,
    onLinesChange: (List<PaymentLineDraft>) -> Unit,
    onRegister: () -> Unit,
    defaultUsdAmount: String = "",
    perClientPendingCount: Int = 0,
    perClientPaidLabel: String? = null,
    initialPaymentLines: List<PaymentLineDraft> = emptyList(),
    pendingUsdInput: String = "",
    onPendingUsdChange: (String) -> Unit = {},
    onChangeGivenCentsChange: (Long) -> Unit = {},
) {
    val allCourtesy = queueSize > 0 && grossChargeableTotal <= 0
    val walletCoversAll = walletModuleEnabled &&
        grossChargeableTotal > 0 &&
        chargeableTotal <= 0 &&
        walletAppliedTotal > 0
    val perClient = mode == BulkPaymentMode.PerClient
    val perClientReady = perClient && perClientPendingCount == 0

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.Primary.copy(alpha = 0.06f))
            .border(1.5.dp, AdminColors.Primary.copy(alpha = 0.25f), RoundedCornerShape(16.dp))
            .padding(16.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Outlined.Payments, null, tint = AdminColors.Primary, modifier = Modifier.size(22.dp))
            Spacer(Modifier.width(8.dp))
            Text("Cobro de la lista", fontWeight = FontWeight.Bold, fontSize = 17.sp, color = AdminColors.Text)
        }
        Spacer(Modifier.height(10.dp))
        // Solo cobro por socio: cada persona tiene su pago (se quitó «Un solo pago para varios»).
        Spacer(Modifier.height(14.dp))

        if (queueSize == 0) {
            Text(
                if (perClient) {
                    if (walletModuleEnabled) {
                        "Busca un socio, elige plan y entrenador, configura su cobro (incluido el monedero) " +
                            "y agrégalo a la lista. Al final confirmas el cobro de todos."
                    } else {
                        "Busca un socio, elige plan y entrenador, configura su cobro y agrégalo a la lista. " +
                            "Al final confirmas el cobro de todos."
                    }
                } else {
                    if (walletModuleEnabled) {
                        "Agrega los socios a la lista y aquí defines un único cobro que los cubre a todos: " +
                            "efectivo, pago móvil, divisas ($)… El excedente va al monedero digital."
                    } else {
                        "Agrega los socios a la lista y aquí defines un único cobro que los cubre a todos: " +
                            "efectivo, pago móvil, divisas ($)…"
                    }
                },
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
        } else if (perClient) {
            BulkPaymentPerClientSummary(
                grossChargeableTotal = grossChargeableTotal,
                walletAppliedTotal = walletAppliedTotal,
                chargeableTotal = chargeableTotal,
                chargeableDualLabel = chargeableDualLabel,
                chargeableCount = chargeableCount,
                pendingCount = perClientPendingCount,
                paidLabel = perClientPaidLabel,
                registerWithoutPayment = registerWithoutPayment,
            )
        } else if (allCourtesy) {
            Text(
                "Todos en la lista son cortesía. No hace falta método de pago.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
            Spacer(Modifier.height(8.dp))
            Text("Total: Bs. 0,00", fontWeight = FontWeight.Bold, fontSize = 22.sp, color = AdminColors.Primary)
        } else if (registerWithoutPayment) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Surface)
                    .padding(16.dp),
            ) {
                Text("Total pendiente", color = AdminColors.TextMuted, fontSize = 12.sp)
                Text(
                    formatMoney(grossChargeableTotal.toInt(), GymlyCurrency.CODE),
                    fontWeight = FontWeight.Bold,
                    fontSize = 28.sp,
                    color = AdminColors.Primary,
                )
                Spacer(Modifier.height(8.dp))
                Text(
                    "Las órdenes quedarán pendientes de cobro. La membresía se activará cuando se pague el 100%.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                )
            }
        } else {
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Surface)
                    .padding(16.dp),
            ) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Bottom,
                ) {
                    Column {
                        Text(
                            if (walletCoversAll) "Cubierto por monedero" else "Total a cobrar",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                        Text(
                            chargeableDualLabel,
                            fontWeight = FontWeight.Bold,
                            fontSize = 28.sp,
                            color = AdminColors.Primary,
                        )
                        if (walletModuleEnabled && walletAppliedTotal > 0) {
                            Text(
                                "Plan ${formatMoney(grossChargeableTotal.toInt(), GymlyCurrency.CODE)}" +
                                    " − monedero ${formatMoney(walletAppliedTotal.toInt(), GymlyCurrency.CODE)}",
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                    Text(
                        "$chargeableCount ${if (chargeableCount == 1) "socio" else "socios"}",
                        color = AdminColors.TextMuted,
                        fontSize = 13.sp,
                    )
                }
                if (paymentConfigured && !paymentSummary.isNullOrBlank()) {
                    Spacer(Modifier.height(6.dp))
                    Text("Resumen: $paymentSummary", color = AdminColors.Primary, fontSize = 12.sp, fontWeight = FontWeight.Medium)
                }
                if (walletCoversAll) {
                    Spacer(Modifier.height(12.dp))
                    Text(
                        "El saldo del monedero digital cubre el total. Puedes registrar sin agregar métodos de pago.",
                        color = AdminColors.Primary,
                        fontSize = 13.sp,
                    )
                } else {
                    Spacer(Modifier.height(12.dp))
                    Text(
                        buildString {
                            append("Este cobro cubre a los $chargeableCount socios de la lista.")
                            if (walletModuleEnabled) {
                                append(" Si alguno tiene monedero digital, aplícalo en su fila de arriba.")
                                append(" Si pagan de más, el excedente va al monedero digital.")
                            }
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                    Spacer(Modifier.height(16.dp))
                    Text("Pendiente (\$)", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Vacío = pago completo para todos. Con monto, cada orden queda pendiente en dólares.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                    Spacer(Modifier.height(6.dp))
                    OutlinedTextField(
                        value = pendingUsdInput,
                        onValueChange = onPendingUsdChange,
                        enabled = !processing,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        placeholder = { Text("Ej. 10,00 (vacío = completo)") },
                        prefix = { Text("\$ ", color = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                    )
                    Spacer(Modifier.height(12.dp))
                    key(chargeableTotalInput, defaultUsdAmount) {
                        CombinedPaymentLinesForm(
                            totalAmountInput = chargeableTotalInput,
                            defaultLineAmount = chargeableTotalInput,
                            defaultUsdAmount = defaultUsdAmount,
                            enabled = !processing,
                            allowOverpay = true,
                            enforcePlanCoverage = false,
                            multiClientOverpay = chargeableCount > 1,
                            checkExistingMobilePayments = true,
                            showWalletBanner = false,
                            initialLines = initialPaymentLines,
                            onLinesChange = onLinesChange,
                            onChangeGivenCentsChange = onChangeGivenCentsChange,
                        ) { _, _, _, _ -> }
                    }
                }
            }
        }

        if (grossChargeableTotal > 0 && queueSize > 0) {
            Spacer(Modifier.height(12.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .clickable(enabled = !processing) { onRegisterWithoutPaymentChange(!registerWithoutPayment) }
                    .padding(vertical = 4.dp),
            ) {
                Checkbox(
                    checked = registerWithoutPayment,
                    onCheckedChange = { if (!processing) onRegisterWithoutPaymentChange(it) },
                    enabled = !processing,
                    colors = CheckboxDefaults.colors(
                        checkedColor = AdminColors.Primary,
                        uncheckedColor = AdminColors.Outline,
                    ),
                )
                Column {
                    Text("Registrar sin cobrar ahora", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
                    Text(
                        "Crea las órdenes en pendiente. Cobras después desde Pagos u Órdenes de membresía.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
        }

        paymentError?.let {
            Spacer(Modifier.height(8.dp))
            Text(it, color = AdminColors.Error, fontSize = 12.sp)
        }
        Spacer(Modifier.height(12.dp))
        Button(
            onClick = onRegister,
            enabled = !processing && queueSize > 0 &&
                (!perClient || registerWithoutPayment || perClientReady),
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 56.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = AdminColors.Primary,
                contentColor = AdminColors.OnBrand,
                disabledContainerColor = AdminColors.Outline.copy(alpha = 0.4f),
            ),
            shape = RoundedCornerShape(14.dp),
            elevation = ButtonDefaults.buttonElevation(
                defaultElevation = 4.dp,
                pressedElevation = 1.dp,
            ),
        ) {
            Icon(
                Icons.Outlined.Payments,
                contentDescription = null,
                modifier = Modifier.size(24.dp),
                tint = AdminColors.OnBrand,
            )
            Spacer(Modifier.width(10.dp))
            Text(
                when {
                    processing -> "Registrando…"
                    queueSize == 0 -> "Agrega socios a la lista"
                    registerWithoutPayment -> "Registrar pendiente ($queueSize)"
                    perClient && perClientPendingCount > 0 ->
                        "Falta el cobro de $perClientPendingCount " +
                            if (perClientPendingCount == 1) "socio" else "socios"
                    perClient -> "Confirmar cobro de $queueSize " +
                        if (queueSize == 1) "socio" else "socios"
                    walletCoversAll -> "Registrar con monedero ($queueSize)"
                    chargeableTotal > 0 && !paymentConfigured -> "Selecciona método y registrar"
                    else -> "Cobrar y registrar ($queueSize)"
                },
                fontWeight = FontWeight.Bold,
                fontSize = 17.sp,
            )
        }
    }
}

@Composable
private fun MembershipFieldDropdown(
    label: String,
    value: String,
    options: List<Pair<String?, String>>,
    onSelect: (String?) -> Unit,
    modifier: Modifier = Modifier,
) {
    var open by remember { mutableStateOf(false) }
    Column(modifier) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        Spacer(Modifier.height(4.dp))
        Box {
            OutlinedButton(
                onClick = { open = true },
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(value, color = AdminColors.Text, maxLines = 1, fontSize = 13.sp)
            }
            DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
                options.forEach { (id, text) ->
                    DropdownMenuItem(
                        text = { Text(text) },
                        onClick = {
                            onSelect(id)
                            open = false
                        },
                    )
                }
            }
        }
    }
}

/** Resumen corto del cobro de un socio: monedero + métodos + totales pagados. */
private fun bulkPaymentRowSummary(
    lines: List<PaymentLineDraft>,
    walletApplyCents: Long,
    defaultAmount: String,
): String? {
    val parts = buildList {
        if (walletApplyCents > 0) {
            add("Monedero ${formatMoney(walletApplyCents.toInt(), GymlyCurrency.CODE)}")
        }
        if (lines.isNotEmpty()) {
            add(paymentLinesSummaryLabel(lines, defaultAmount))
            val paid = paymentLinesPaidTotalsLabel(lines, defaultAmount)
            if (paid != "—" && paid.isNotBlank()) add(paid)
        }
    }
    return parts.joinToString(" · ").ifBlank { null }
}

/**
 * Reajusta los montos en Bs al nuevo total conservando métodos y referencias:
 * la última línea en Bs absorbe la diferencia (p. ej. tras usar saldo del monedero).
 */
private fun rescalePaymentLinesToTotal(
    lines: List<PaymentLineDraft>,
    totalCents: Long,
): List<PaymentLineDraft> {
    if (lines.isEmpty()) return lines
    val bsIndexes = lines.indices.filter { !lines[it].method.usesUsdAmount() }
    if (bsIndexes.isEmpty()) return lines
    val last = bsIndexes.last()
    val assignedBefore = bsIndexes.dropLast(1).sumOf { parseBulkAmountToCents(lines[it].amountInput) }
    val remainder = (totalCents - assignedBefore).coerceAtLeast(0L)
    return lines.mapIndexed { index, line ->
        if (index == last) line.copy(amountInput = centsToAmountInput(remainder)) else line
    }
}

private fun catalogUsdDueCents(
    items: List<BulkPaymentQueueItem>,
    plans: List<AdminMembershipPlanItem>,
    useFullPlanAmount: Boolean,
): Long = items.sumOf { item ->
    val basis = if (useFullPlanAmount) item.amountCents else item.remainingCents
    if (basis <= 0) return@sumOf 0L
    val planUsd = plans.firstOrNull { it.id == item.planId }?.priceUsdCents ?: 0L
    if (planUsd > 0 && item.amountCents > 0) planUsd * basis / item.amountCents else 0L
}

private fun checkoutUsdDefaultAmount(
    items: List<BulkPaymentQueueItem>,
    plans: List<AdminMembershipPlanItem>,
    dueBsCents: Long,
    quote: AdminExchangeRateQuote?,
    useFullPlanAmount: Boolean,
): String = resolveCheckoutUsdDefaultInput(
    dueBsCents,
    catalogUsdDueCents(items, plans, useFullPlanAmount),
    quote,
)

/**
 * Excedente en Bs que no tiene vuelto declarado. Cero si lo pagado no supera lo debido o el vuelto lo cubre.
 * Todos los montos en centavos de Bs.
 */
internal fun excedenteBsSinVuelto(pagadoBsCents: Long, debidoCents: Long, vueltoCents: Long): Long {
    val excess = pagadoBsCents - debidoCents
    return if (excess > 0L) excess - minOf(vueltoCents, excess) else 0L
}

/** Texto de resumen: vuelto y/o excedente a monedero (no atribuye el vuelto al monedero). */
private fun overpayDispositionNote(changeGivenCents: Long, walletCreditCents: Long): String = buildString {
    if (changeGivenCents > 0) {
        append(" · vuelto ")
        append(formatMoney(changeGivenCents.toInt(), GymlyCurrency.CODE))
    }
    if (walletCreditCents > 0) {
        append(" · excedente ")
        append(formatMoney(walletCreditCents.toInt(), GymlyCurrency.CODE))
        append(" → monedero")
    }
}
